import { ENQUIRE_MODAL_SHOWUP_KEY } from '~/modules/enquiry/constants';
import { isCleanRedirect } from '@modules/root';

const LOGIN_ROUTES = ['/login', '/login-test'] as const;

interface GetPostAuthDestinationOptions {
    pathname: string;
    search: string;
    redirect?: string;
    allowExternalUrls?: boolean;
    skipPostSignUp?: boolean;
    encodedRedirect?: string | null;
    buildHubFeatureEnabled: boolean;
    /**
     * True when the member already has access to at least one non-personal BuildHub —
     * either as owner or as a collaborator. Drives the create-project onboarding gate:
     * collaborator-only members (e.g. HDE guests stitched in via
     * `claimInvitedBuildHubCollaborators`) skip onboarding so the original invite
     * redirect can land them on the homeowner's project.
     */
    hasBuildHubs: boolean;
    onboardingDismissedAt?: string | null;
}

const ONBOARDING_DISMISSAL_COOLDOWN_DAYS = 30;
const DAY_IN_MS = 24 * 60 * 60 * 1000;

interface ShouldStartBuildHubOnboardingOptions {
    hasBuildHubs: boolean;
    onboardingDismissedAt?: string | null;
    now?: number;
}

/**
 * Returns true when Build Hub onboarding should be shown to the member.
 * `undefined` keeps the legacy behaviour for call sites that do not yet load
 * the dismissal timestamp.
 */
export const shouldStartBuildHubOnboarding = ({
    hasBuildHubs,
    onboardingDismissedAt,
    now = Date.now(),
}: ShouldStartBuildHubOnboardingOptions): boolean => {
    if (hasBuildHubs) {
        return false;
    }

    if (onboardingDismissedAt === undefined || onboardingDismissedAt === null) {
        return true;
    }

    const dismissedAtMs = Date.parse(onboardingDismissedAt);
    if (Number.isNaN(dismissedAtMs)) {
        return true;
    }

    return (
        now - dismissedAtMs >= ONBOARDING_DISMISSAL_COOLDOWN_DAYS * DAY_IN_MS
    );
};

/**
 * Returns true when the current route is one of the full-page login routes.
 */
export const isLoginRoute = (pathname: string): boolean => {
    return LOGIN_ROUTES.includes(pathname as (typeof LOGIN_ROUTES)[number]);
};

/**
 * Builds a login URL with a preserved redirect target so the login loader can
 * perform the final server-side redirect once the session is established.
 */
export const getLoginRouteWithRedirect = (
    redirect: string,
    loginRoute = '/login',
): string => {
    const separator = loginRoute.includes('?') ? '&' : '?';
    return `${loginRoute}${separator}redirect=${encodeURIComponent(redirect)}`;
};

/**
 * Resolves the redirect target a logged-in user should be sent to when they
 * hit a login page with an optional redirect query parameter.
 */
export const getAuthenticatedLoginRedirectUrl = (
    redirectParam?: string | null,
): string | null => {
    if (!redirectParam || !isCleanRedirect(redirectParam)) {
        return null;
    }

    try {
        const redirectUrl = decodeURI(redirectParam);
        return redirectUrl.startsWith('/') ? redirectUrl : `/${redirectUrl}`;
    } catch {
        return null;
    }
};

const getBaseRequestedRedirect = ({
    pathname,
    search,
    redirect,
    allowExternalUrls,
}: Pick<
    GetPostAuthDestinationOptions,
    'pathname' | 'search' | 'redirect' | 'allowExternalUrls'
>): string => {
    const requestedRedirect =
        redirect && isCleanRedirect(redirect, allowExternalUrls)
            ? decodeURI(redirect)
            : `${pathname}${search}`;

    if (isLoginRoute(pathname) && requestedRedirect === pathname) {
        return '/';
    }

    return requestedRedirect;
};

const getOnboardingRedirectParam = (redirect: string): string => {
    if (!redirect.includes(ENQUIRE_MODAL_SHOWUP_KEY)) {
        return encodeURI(redirect);
    }

    const url = new URL(redirect, 'http://localhost');
    url.searchParams.delete(ENQUIRE_MODAL_SHOWUP_KEY);

    return encodeURI(`${url.pathname}${url.search}${url.hash}`);
};

/**
 * Resolves the final post-auth destination, including Build Hub onboarding
 * routing and nested redirect preservation.
 */
export const getPostAuthDestination = ({
    pathname,
    search,
    redirect,
    allowExternalUrls = false,
    skipPostSignUp = false,
    encodedRedirect,
    buildHubFeatureEnabled,
    hasBuildHubs,
    onboardingDismissedAt,
}: GetPostAuthDestinationOptions): string => {
    const requestedRedirect = getBaseRequestedRedirect({
        pathname,
        search,
        redirect,
        allowExternalUrls,
    });

    if (encodedRedirect) {
        return `/linkshare?encoded=${encodedRedirect}`;
    }

    if (skipPostSignUp) {
        return requestedRedirect;
    }

    const searchParams = new URLSearchParams();

    if (requestedRedirect !== '/') {
        searchParams.set(
            'redirect',
            getOnboardingRedirectParam(requestedRedirect),
        );
    }

    const onboardingSearch = searchParams.toString();

    if (buildHubFeatureEnabled) {
        if (
            shouldStartBuildHubOnboarding({
                hasBuildHubs,
                onboardingDismissedAt,
            })
        ) {
            return `/app/create-project${
                onboardingSearch ? `?${onboardingSearch}` : ''
            }`;
        }

        return requestedRedirect;
    }

    return `/post-sign-up${onboardingSearch ? `?${onboardingSearch}` : ''}`;
};
